import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment, OutlinedInput } from "@mui/material";
import classNames from "classnames";
import React, { type ChangeEvent } from "react";

import { useDebouncedCallback } from "@/shared/hooks";

import type { KeywordFilter } from "../../Topics/filter";

import styles from "./FilterInput.module.css";
import { describeRegexError } from "./regexUtils";

/**
 * Imperative handle exposed via `controlsRef` for parents that need to drive
 * the input externally (e.g., resetting the regex toggle when an external
 * filter request populates the term).
 */
export interface FilterInputControls {
  /** Reset the regex toggle to off without modifying the term. */
  clearRegex: () => void;
  /**
   * Set the input's term programmatically (e.g., from an external request).
   * Does not fire `onFilterChange` — the caller is responsible for syncing
   * its own filter state alongside this call.
   */
  setTerm: (term: string) => void;
}

interface FilterInputProps {
  /** Callback when filter changes. Term changes are debounced 300ms; toggle
   * changes fire immediately. */
  onFilterChange: (filter: KeywordFilter) => void;

  /** Optional class name to apply to the root element */
  className?: classNames.Argument;

  /** Placeholder text for the input. Defaults to "Filter by name" */
  placeholder?: string;

  /** Imperative controls handle. */
  controlsRef?: React.Ref<FilterInputControls>;
}

const REGEX_ERROR_ID = "filter-input-regex-error";

export function FilterInput({
  onFilterChange,
  className,
  placeholder = "Filter by name",
  controlsRef,
}: FilterInputProps) {
  const [isRegex, setIsRegex] = React.useState(false);
  // Controlled value: `term` is the source of truth for what's in the input,
  // updated synchronously on every keystroke. The emission to consumers is
  // debounced separately, but validation (regex error border + tooltip) runs
  // against this live value so the user sees feedback as they type.
  const [term, setTerm] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const onFilterChangeRef = React.useRef(onFilterChange);
  React.useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
  }, [onFilterChange]);

  React.useImperativeHandle(
    controlsRef,
    () => ({
      clearRegex: () => setIsRegex(false),
      setTerm: (next: string) => setTerm(next),
    }),
    [],
  );

  const emit = React.useCallback((nextTerm: string, nextIsRegex: boolean) => {
    onFilterChangeRef.current({
      type: "keyword",
      term: nextTerm,
      isRegex: nextIsRegex,
    });
  }, []);

  const emitDebounced = useDebouncedCallback(emit, 300);

  const handleChange = React.useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextTerm = event.target.value;
      setTerm(nextTerm);
      emitDebounced(nextTerm, isRegex);
    },
    [emitDebounced, isRegex],
  );

  const handleToggleRegex = React.useCallback(() => {
    const next = !isRegex;
    // Drop any keystroke emit queued with the previous `isRegex` so it
    // can't fire after this toggle and overwrite the new state.
    emitDebounced.cancel();
    emit(term, next);
    setIsRegex(next);
    // Send focus back to the input so the user can keep typing after
    // flipping the toggle. Without this, an unfocused input stays
    // unfocused (mousedown's preventDefault below blocks the browser's
    // default focus-to-button, but doesn't itself focus the input).
    inputRef.current?.focus();
  }, [emit, emitDebounced, isRegex, term]);

  // Block focus from moving to the toggle button when clicked. MUI's
  // InputAdornment doesn't focus the input on its own, so without this
  // the caret jumps out of the input mid-typing. `handleToggleRegex`
  // explicitly focuses the input afterward to handle the not-yet-focused
  // case.
  const handleToggleMouseDown = React.useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  const regexError = React.useMemo(() => {
    if (!isRegex || term === "") {
      return undefined;
    }
    return describeRegexError(term);
  }, [isRegex, term]);

  const hasError = regexError !== undefined;

  return (
    <div className={classNames(className)}>
      <OutlinedInput
        value={term}
        size="small"
        fullWidth
        placeholder={placeholder}
        error={hasError}
        className={styles.input}
        inputRef={inputRef}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon className={styles.icon} />
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position="end">
            <button
              type="button"
              className={classNames(
                styles["regex-toggle"],
                isRegex && styles["regex-toggle-active"],
              )}
              onClick={handleToggleRegex}
              onMouseDown={handleToggleMouseDown}
              title="Toggle regex"
              aria-label="Toggle regex"
              aria-pressed={isRegex}
            >
              .*
            </button>
          </InputAdornment>
        }
        onChange={handleChange}
        inputProps={{
          className: styles["input-base-child"],
          "aria-invalid": hasError,
          "aria-describedby": hasError ? REGEX_ERROR_ID : undefined,
        }}
      />
      {hasError && (
        <div id={REGEX_ERROR_ID} role="alert" className={styles["regex-error"]}>
          Invalid regex: {regexError}
        </div>
      )}
    </div>
  );
}
